import React, { useState, useRef, useEffect } from "react";
import {
  User,
  Camera,
  Save,
  Edit3,
  Mail,
  Phone,
  Calendar,
  Shield,
  Key,
  Eye,
  EyeOff,
  Settings as SettingsIcon,
  Globe,
  Database,
  RefreshCw,
  Send,
  Copy,
  Check,
  CheckCircle,
  AlertTriangle,
  Lock,
  Sliders,
  Terminal,
  Activity
} from "react-feather";
import { auth, db, uploadImage, updateUserDatabase } from "shared/utils/firebase";
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, sendPasswordResetEmail } from "firebase/auth";
import { collection, query, where, getDocs, doc, setDoc, getDoc, addDoc } from "firebase/firestore";
import PageHeader from "shared/components/PageHeader/PageHeader";
import styles from "./AdminProfile.module.css";

function AdminProfile({ userDetails, auth: isAuthenticated }) {
  const imagePicker = useRef();
  const [activeTab, setActiveTab] = useState("profile"); // "profile" | "platform_settings" | "audit_logs"
  const [editing, setEditing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [profileImageUploadStarted, setProfileImageUploadStarted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [statusMsg, setStatusMsg] = useState({ type: "", text: "" });
  const [copiedUid, setCopiedUid] = useState(false);

  // Admin Profile Form Data (Stripped of redundant student fields like streams, student badges, courses)
  const [profileData, setProfileData] = useState({
    name: userDetails?.name || "",
    email: userDetails?.email || "",
    phone: (userDetails?.phone && userDetails.phone !== userDetails.email) ? userDetails.phone : "",
    designation: userDetails?.designation || "Platform Administrator",
    department: userDetails?.department || "IT & System Administration",
    adminRoleTitle: userDetails?.adminRoleTitle || "Super Administrator",
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const [profileImageUrl, setProfileImageUrl] = useState(
    userDetails?.profileImage || "/static_img/DP.jpg"
  );

  // Platform & System Settings State (Merged from Settings.js)
  const [platformSettings, setPlatformSettings] = useState({
    platformName: "Silicon Learning Platform",
    platformDescription: "Advanced VLSI & Embedded Systems Engineering Platform",
    emailNotifications: true,
    emailFrom: "admin@siliconlogix.com",
    passwordMinLength: 8,
    requireEmailVerification: true,
    allowUserRegistration: true,
    requireAdminApproval: false,
    maxFileSize: "10MB",
    enableAnalytics: true,
    maintenanceMode: false,
    maintenanceMessage: "We are currently conducting scheduled system maintenance. Please check back shortly."
  });

  const [adminAuditLogs, setAdminAuditLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);

  // Fetch Platform Settings & Admin Audit Logs
  useEffect(() => {
    fetchSystemSettings();
    if (userDetails?.uid) {
      fetchAdminAuditLogs(userDetails.uid);
    }
  }, [userDetails?.uid]);

  const fetchSystemSettings = async () => {
    try {
      const setDocSnap = await getDoc(doc(db, "systemSettings", "general"));
      if (setDocSnap.exists()) {
        setPlatformSettings(prev => ({ ...prev, ...setDocSnap.data() }));
      }
    } catch (err) {
      console.warn("Notice reading systemSettings:", err);
    }
  };

  const fetchAdminAuditLogs = async (adminUid) => {
    try {
      setIsLoadingLogs(true);
      const logsSnap = await getDocs(query(collection(db, "adminActions"), where("performedBy", "==", adminUid)));
      const logs = logsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.performedAt?.toDate() || 0) - (a.performedAt?.toDate() || 0));
      setAdminAuditLogs(logs);
    } catch (err) {
      console.warn("Error fetching admin audit logs:", err);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPlatformSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCameraClick = () => {
    if (imagePicker.current) imagePicker.current.click();
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setProfileImageUploadStarted(true);
    uploadImage(
      file,
      (progress) => {
        setProgress(progress);
      },
      (url) => {
        setProfileImageUrl(url);
        updateUserDatabase(
          { ...profileData, profileImage: url },
          userDetails.uid
        );
        setProfileImageUploadStarted(false);
        setProgress(0);
      },
      (err) => {
        console.error("Error uploading photo:", err);
        setProfileImageUploadStarted(false);
      },
      "admin_profile"
    );
  };

  // Save Admin Profile Details & Password Changes
  const handleSaveProfile = async () => {
    try {
      setSaveStatus("saving");
      setStatusMsg({ type: "", text: "" });

      const updatedData = {
        ...profileData,
        name: profileData.name.trim(),
        profileImage: profileImageUrl,
        updatedAt: new Date()
      };

      // Handle Password Change
      if (profileData.newPassword) {
        if (!profileData.currentPassword) {
          setSaveStatus("error");
          setStatusMsg({ type: "danger", text: "Current password is required to set a new password" });
          return;
        }

        if (profileData.newPassword !== profileData.confirmPassword) {
          setSaveStatus("error");
          setStatusMsg({ type: "danger", text: "New passwords do not match" });
          return;
        }

        if (profileData.newPassword.length < 6) {
          setSaveStatus("error");
          setStatusMsg({ type: "danger", text: "Password must be at least 6 characters" });
          return;
        }

        try {
          const credential = EmailAuthProvider.credential(
            auth.currentUser.email,
            profileData.currentPassword
          );
          await reauthenticateWithCredential(auth.currentUser, credential);
          await updatePassword(auth.currentUser, profileData.newPassword);

          setStatusMsg({ type: "success", text: "Password updated successfully!" });

          delete updatedData.newPassword;
          delete updatedData.confirmPassword;
          delete updatedData.currentPassword;
        } catch (error) {
          setSaveStatus("error");
          setStatusMsg({ type: "danger", text: "Failed to update password: " + error.message });
          return;
        }
      } else {
        delete updatedData.newPassword;
        delete updatedData.confirmPassword;
        delete updatedData.currentPassword;
      }

      await updateUserDatabase(updatedData, userDetails.uid);

      await addDoc(collection(db, "adminActions"), {
        action: "ADMIN_PROFILE_UPDATED",
        performedBy: userDetails.uid,
        performedAt: new Date()
      });

      setSaveStatus("success");
      setEditing(false);
      setStatusMsg({ type: "success", text: "Admin profile updated successfully!" });

      setProfileData(prev => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      }));
    } catch (error) {
      console.error("Error updating admin profile:", error);
      setSaveStatus("error");
      setStatusMsg({ type: "danger", text: "Failed to update profile: " + error.message });
    }
  };

  // Save Merged Platform System Settings
  const handleSavePlatformSettings = async () => {
    try {
      setIsSavingSettings(true);
      setStatusMsg({ type: "", text: "" });

      await setDoc(doc(db, "systemSettings", "general"), {
        ...platformSettings,
        updatedAt: new Date(),
        updatedBy: userDetails?.uid || "admin"
      }, { merge: true });

      await addDoc(collection(db, "adminActions"), {
        action: "PLATFORM_SETTINGS_UPDATED",
        performedBy: userDetails?.uid,
        performedAt: new Date()
      });

      setStatusMsg({ type: "success", text: "Platform & system settings saved successfully!" });
    } catch (err) {
      console.error("Error saving platform settings:", err);
      setStatusMsg({ type: "danger", text: "Failed to save settings: " + err.message });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSendPasswordResetSelf = async () => {
    if (!userDetails?.email) return;
    try {
      setIsSendingReset(true);
      await sendPasswordResetEmail(auth, userDetails.email);
      setStatusMsg({ type: "success", text: `Password reset link dispatched to ${userDetails.email}` });
    } catch (err) {
      console.error("Password reset error:", err);
      setStatusMsg({ type: "danger", text: "Error sending reset email: " + err.message });
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleCopyUid = (uid) => {
    navigator.clipboard.writeText(uid);
    setCopiedUid(true);
    setTimeout(() => setCopiedUid(false), 2000);
  };

  if (!isAuthenticated || !userDetails) {
    return (
      <div className={styles.loadingContainer}>
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2">Loading Admin Profile...</p>
      </div>
    );
  }

  return (
    <div className={styles.adminProfile}>
      {/* Top Header */}
      <PageHeader
        icon={User}
        title="Admin Profile & Control Center"
        subtitle="Manage administrator credentials, security, and global platform system configurations"
        badge={userDetails.role || "Super Admin"}
        actions={
          <>
            {activeTab === "profile" && (
              editing ? (
                <div className="d-flex gap-2">
                  <button className={styles.btnSecondary} onClick={() => setEditing(false)}>
                    Cancel
                  </button>
                  <button className={styles.btnPrimary} onClick={handleSaveProfile}>
                    <Save size={16} /> Save Changes
                  </button>
                </div>
              ) : (
                <button className={styles.btnPrimary} onClick={() => setEditing(true)}>
                  <Edit3 size={16} /> Edit Profile
                </button>
              )
            )}

            {activeTab === "platform_settings" && (
              <button
                className={styles.btnPrimary}
                onClick={handleSavePlatformSettings}
                disabled={isSavingSettings}
              >
                <Save size={16} /> {isSavingSettings ? "Saving Settings..." : "Save System Settings"}
              </button>
            )}
          </>
        }
      />

      {/* Admin Hero Card */}
      <div className={styles.adminHeroCard}>
        <div className={styles.adminHeroLeft}>
          <div className={styles.avatarContainer}>
            <img src={profileImageUrl} alt="Admin Avatar" className={styles.avatarImage} />
            <div className={styles.cameraBtn} onClick={handleCameraClick} title="Upload Photo">
              <Camera size={16} />
            </div>
          </div>
          <div className={styles.adminHeroInfo}>
            <h3>{profileData.name || "Administrator"}</h3>
            <p className={styles.adminHeroSubtitle}>
              <Shield size={14} className="me-1" />
              {profileData.designation} • {profileData.department}
            </p>
            <div className={styles.heroBadges}>
              <span className="badge bg-danger">
                <Shield size={11} className="me-1" /> {userDetails.role || "Admin"}
              </span>
              <span className="badge bg-dark">
                <Calendar size={11} className="me-1" /> Joined: {userDetails.createdAt ? new Date(userDetails.createdAt).toLocaleDateString() : "System Admin"}
              </span>
            </div>
          </div>
        </div>

        <div className="text-end">
          <span className="badge bg-success py-2 px-3 fs-6">
            <CheckCircle size={14} className="me-1" /> Active Session
          </span>
        </div>
      </div>

      {/* Unified Multi-Tab Navigation */}
      <div className={styles.tabNav}>
        <button
          className={`${styles.tabBtn} ${activeTab === 'profile' ? styles.tabBtnActive : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <User size={16} /> Profile & Credentials
        </button>
        <button
          className={`${styles.tabBtn} ${activeTab === 'platform_settings' ? styles.tabBtnActive : ''}`}
          onClick={() => setActiveTab('platform_settings')}
        >
          <SettingsIcon size={16} /> Platform & System Settings
        </button>
        <button
          className={`${styles.tabBtn} ${activeTab === 'audit_logs' ? styles.tabBtnActive : ''}`}
          onClick={() => setActiveTab('audit_logs')}
        >
          <Activity size={16} /> Admin Action Logs ({adminAuditLogs.length})
        </button>
      </div>

      {/* Global Status Alert Banner */}
      {statusMsg.text && (
        <div className={`alert alert-${statusMsg.type} alert-dismissible fade show mb-4`} role="alert">
          {statusMsg.text}
          <button type="button" className="btn-close" onClick={() => setStatusMsg({ type: "", text: "" })} />
        </div>
      )}

      {/* TAB 1: ADMIN PROFILE & SECURITY */}
      {activeTab === 'profile' && (
        <div>
          <div className={styles.sectionCard}>
            <h5 className={styles.sectionTitle}>
              <User size={18} className="me-2 text-primary" /> Personal & Account Information
            </h5>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Admin Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={profileData.name}
                  onChange={handleInputChange}
                  disabled={!editing}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Admin Email (Read-only)</label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  disabled={true}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Mobile Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleInputChange}
                  disabled={!editing}
                  className={styles.formInput}
                  placeholder="+91 9876543210"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Designation Title</label>
                <input
                  type="text"
                  name="designation"
                  value={profileData.designation}
                  onChange={handleInputChange}
                  disabled={!editing}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Department / Unit</label>
                <input
                  type="text"
                  name="department"
                  value={profileData.department}
                  onChange={handleInputChange}
                  disabled={!editing}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>System UID</label>
                <div className="d-flex align-items-center">
                  <input
                    type="text"
                    value={userDetails.uid || userDetails.id || ""}
                    disabled={true}
                    className={`${styles.formInput} me-2`}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => handleCopyUid(userDetails.uid || userDetails.id)}
                  >
                    {copiedUid ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Admin Security & Password Change */}
          <div className={styles.sectionCard}>
            <h5 className={styles.sectionTitle}>
              <Key size={18} className="me-2 text-primary" /> Admin Security & Password Control
            </h5>

            <div className="d-flex align-items-center justify-content-between mb-3 p-3 border rounded bg-light dark:bg-dark">
              <div>
                <h6 className="fw-bold mb-1">Dispatch Password Reset Link</h6>
                <p className="text-muted small mb-0">Receive a secure reset link at registered email ({userDetails.email}).</p>
              </div>
              <button
                type="button"
                className="btn btn-outline-warning btn-sm fw-bold"
                onClick={handleSendPasswordResetSelf}
                disabled={isSendingReset}
              >
                <Send size={13} className="me-1" /> {isSendingReset ? "Sending..." : "Send Reset Email"}
              </button>
            </div>

            {editing && (
              <div className="pt-2">
                <h6 className="fw-bold mb-3 text-primary">Direct Update Admin Password</h6>
                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Current Password *</label>
                    <div className="position-relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="currentPassword"
                        value={profileData.currentPassword}
                        onChange={handleInputChange}
                        className={styles.formInput}
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        className="btn btn-sm text-secondary position-absolute end-0 top-50 translate-middle-y me-2"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>New Password *</label>
                    <input
                      type="password"
                      name="newPassword"
                      value={profileData.newPassword}
                      onChange={handleInputChange}
                      className={styles.formInput}
                      placeholder="Min 6 characters"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Confirm New Password *</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={profileData.confirmPassword}
                      onChange={handleInputChange}
                      className={styles.formInput}
                      placeholder="Re-type new password"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: PLATFORM & SYSTEM SETTINGS (MERGED FROM SETTINGS.JS) */}
      {activeTab === 'platform_settings' && (
        <div>
          {/* General Platform Config */}
          <div className={styles.sectionCard}>
            <h5 className={styles.sectionTitle}>
              <Globe size={18} className="me-2 text-primary" /> General Platform Configuration
            </h5>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Platform Title Name</label>
                <input
                  type="text"
                  name="platformName"
                  value={platformSettings.platformName}
                  onChange={handleSettingsChange}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>System Outgoing Email Sender</label>
                <input
                  type="email"
                  name="emailFrom"
                  value={platformSettings.emailFrom}
                  onChange={handleSettingsChange}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup} style={{ gridColumn: '1 / -1' }}>
                <label className={styles.formLabel}>Platform Description</label>
                <textarea
                  name="platformDescription"
                  value={platformSettings.platformDescription}
                  onChange={handleSettingsChange}
                  className={styles.formTextarea}
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* User Registration & Security Policies */}
          <div className={styles.sectionCard}>
            <h5 className={styles.sectionTitle}>
              <Shield size={18} className="me-2 text-primary" /> User Access & Registration Policies
            </h5>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="form-check form-switch p-2 border rounded">
                  <input
                    className="form-check-input ms-0 me-2"
                    type="checkbox"
                    id="allowRegSwitch"
                    name="allowUserRegistration"
                    checked={platformSettings.allowUserRegistration}
                    onChange={handleSettingsChange}
                  />
                  <label className="form-check-label fw-bold" htmlFor="allowRegSwitch">
                    Allow New User Sign-ups & Registrations
                  </label>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-check form-switch p-2 border rounded">
                  <input
                    className="form-check-input ms-0 me-2"
                    type="checkbox"
                    id="reqVerifySwitch"
                    name="requireEmailVerification"
                    checked={platformSettings.requireEmailVerification}
                    onChange={handleSettingsChange}
                  />
                  <label className="form-check-label fw-bold" htmlFor="reqVerifySwitch">
                    Mandatory Email Verification on Sign-up
                  </label>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-check form-switch p-2 border rounded">
                  <input
                    className="form-check-input ms-0 me-2"
                    type="checkbox"
                    id="emailNotifSwitch"
                    name="emailNotifications"
                    checked={platformSettings.emailNotifications}
                    onChange={handleSettingsChange}
                  />
                  <label className="form-check-label fw-bold" htmlFor="emailNotifSwitch">
                    Send Automated Platform Email Notifications
                  </label>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-check form-switch p-2 border rounded">
                  <input
                    className="form-check-input ms-0 me-2"
                    type="checkbox"
                    id="analyticsSwitch"
                    name="enableAnalytics"
                    checked={platformSettings.enableAnalytics}
                    onChange={handleSettingsChange}
                  />
                  <label className="form-check-label fw-bold" htmlFor="analyticsSwitch">
                    Enable Platform Usage Analytics Tracking
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* System Maintenance Mode */}
          <div className={styles.sectionCard}>
            <h5 className={styles.sectionTitle}>
              <AlertTriangle size={18} className="me-2 text-warning" /> System Maintenance Control
            </h5>
            <div className="form-check form-switch mb-3 p-2 border rounded bg-light dark:bg-dark">
              <input
                className="form-check-input ms-0 me-2"
                type="checkbox"
                id="maintenanceSwitch"
                name="maintenanceMode"
                checked={platformSettings.maintenanceMode}
                onChange={handleSettingsChange}
              />
              <label className="form-check-label fw-bold text-danger" htmlFor="maintenanceSwitch">
                {platformSettings.maintenanceMode ? "⚠️ System Maintenance Mode ACTIVE (User Access Restricted)" : "✅ Platform Operational (Normal Access)"}
              </label>
            </div>

            {platformSettings.maintenanceMode && (
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Maintenance Notice Banner Message</label>
                <textarea
                  name="maintenanceMessage"
                  value={platformSettings.maintenanceMessage}
                  onChange={handleSettingsChange}
                  className={styles.formTextarea}
                  rows={2}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: ADMIN ACTION AUDIT LOGS */}
      {activeTab === 'audit_logs' && (
        <div className={styles.sectionCard}>
          <h5 className={styles.sectionTitle}>
            <Activity size={18} className="me-2 text-primary" /> Admin Administrative Audit Log
          </h5>

          {isLoadingLogs ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status" />
              <p className="small text-muted mt-2">Loading audit logs...</p>
            </div>
          ) : adminAuditLogs.length > 0 ? (
            <div className={styles.auditList}>
              {adminAuditLogs.map(log => (
                <div key={log.id} className={styles.auditCard}>
                  <div className={styles.auditHeader}>
                    <span className={styles.auditTitle}>{log.action}</span>
                    <span className={styles.auditDate}>
                      {log.performedAt?.toDate ? log.performedAt.toDate().toLocaleString() : "Recently"}
                    </span>
                  </div>
                  {log.targetUserEmail && (
                    <div className="text-muted small">
                      Target User: <strong>{log.targetUserName || log.targetUserEmail}</strong> ({log.targetUserEmail})
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted mb-0 py-3 text-center">No administrative action logs recorded for this account yet.</p>
          )}
        </div>
      )}

      <input
        ref={imagePicker}
        type="file"
        style={{ display: "none" }}
        onChange={handleImageChange}
        accept="image/*"
      />
    </div>
  );
}

export default AdminProfile;
